import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InstallAppPrompt from '../components/InstallAppPrompt';
import { authPublicApi, webApi } from '../services/api';

const TERMS_URL = 'https://www.tippay.co.in/terms';
const SITE_URL = 'https://www.tippay.co.in';

const RESEND_SECONDS = 30;

function isEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s).trim());
}

function formatIdentifierForLogin(raw) {
  const t = String(raw).trim();
  if (isEmail(t)) return t;
  const digits = t.replace(/\D/g, '');
  if (digits.length === 10) return `+91${digits}`;
  return t.startsWith('+') ? t : t;
}

function toE164IndiaTenDigits(tenDigits) {
  const d = String(tenDigits).replace(/\D/g, '');
  if (d.length !== 10) return null;
  return `+91${d}`;
}

function displayE164(phone) {
  const p = String(phone).replace(/\s/g, '');
  if (p.startsWith('+91') && p.length >= 12) {
    const rest = p.slice(3);
    return `+91 ${rest.slice(0, 5)} ${rest.slice(5)}`;
  }
  return p;
}

function persistEmployerSession(token, userPayload) {
  const role = String(userPayload?.role || userPayload?.userType || '').toLowerCase();
  if (role !== 'employer') {
    throw new Error('Only employer accounts can login to this web app');
  }
  const uid = userPayload.userId ?? userPayload.id;
  localStorage.setItem('token', token);
  localStorage.setItem(
    'user',
    JSON.stringify({
      userId: uid,
      id: uid,
      name: userPayload.name,
      email: userPayload.email,
      phone: userPayload.phone,
      role: userPayload.userType || userPayload.role,
      empCode: userPayload.empCode,
      onboardingStatus: userPayload.onboardingStatus
    })
  );
}

function LockIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" className="emp-login-field-icon">
      <path
        fill="currentColor"
        d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"
      />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" className="emp-login-field-icon">
      <path
        fill="currentColor"
        d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
      />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" className="emp-login-field-icon">
      <path
        fill="currentColor"
        d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
      />
    </svg>
  );
}

function EyeIcon({ off }) {
  if (off) {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
        />
      </svg>
    );
  }
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
      />
    </svg>
  );
}

function OtpDigitRow({ value, onChange, disabled }) {
  const refs = useRef([]);
  const setRef = (i) => (el) => {
    refs.current[i] = el;
  };

  const digits = String(value).padEnd(6, ' ').slice(0, 6).split('');

  const focusAt = (i) => {
    const el = refs.current[Math.max(0, Math.min(5, i))];
    if (el) el.focus();
  };

  const applyFullOtp = (str) => {
    const d = str.replace(/\D/g, '').slice(0, 6);
    onChange(d);
    focusAt(d.length >= 6 ? 5 : d.length);
  };

  const onKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const hasDigitHere = index < value.length;
      if (hasDigitHere) {
        onChange(value.slice(0, index) + value.slice(index + 1));
      } else if (index > 0) {
        onChange(value.slice(0, index - 1) + value.slice(index));
        focusAt(index - 1);
      }
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) focusAt(index - 1);
    if (e.key === 'ArrowRight' && index < 5) focusAt(index + 1);
  };

  const onInput = (index, e) => {
    const v = e.target.value.replace(/\D/g, '').slice(-1);
    if (!v) {
      return;
    }
    const next = (value.slice(0, index) + v + value.slice(index + 1)).slice(0, 6);
    onChange(next);
    if (index < 5) focusAt(index + 1);
  };

  const onPaste = (e) => {
    const t = e.clipboardData.getData('text');
    if (/\d/.test(t)) {
      e.preventDefault();
      applyFullOtp(t);
    }
  };

  return (
    <div className="emp-login-otp-row" onPaste={onPaste}>
      {digits.map((ch, i) => (
        <input
          key={i}
          ref={setRef(i)}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          className="emp-login-otp-box"
          value={ch.trim() ? ch : ''}
          disabled={disabled}
          aria-label={`Digit ${i + 1}`}
          onChange={() => {}}
          onInput={(e) => onInput(i, e)}
          onKeyDown={(e) => onKeyDown(i, e)}
        />
      ))}
    </div>
  );
}

function LoginPage() {
  const navigate = useNavigate();
  const [method, setMethod] = useState('password');
  const [step, setStep] = useState('credentials');
  const [identifier, setIdentifier] = useState('');
  const [phoneTen, setPhoneTen] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpPhone, setOtpPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [resendLeft, setResendLeft] = useState(0);

  useEffect(() => {
    if (step !== 'otp' || resendLeft <= 0) return undefined;
    const t = setInterval(() => setResendLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [step, resendLeft]);

  const startOtpTimer = useCallback(() => {
    setResendLeft(RESEND_SECONDS);
  }, []);

  const validatePasswordForm = () => {
    const id = identifier.trim();
    if (!id) {
      setError('Please enter your email or phone number');
      return false;
    }
    if (!isEmail(id) && !/^\d{10}$/.test(id.replace(/\D/g, '').slice(-10))) {
      setError('Please enter a valid email or 10-digit phone number');
      return false;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    return true;
  };

  const validateOtpPhoneForm = () => {
    const d = phoneTen.replace(/\D/g, '');
    if (d.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    return true;
  };

  const onPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!termsAccepted) {
      setError('Please agree to the Terms and Privacy Policy to continue');
      return;
    }
    if (!validatePasswordForm()) return;
    try {
      setLoading(true);
      const formatted = formatIdentifierForLogin(identifier);
      const data = await webApi.login({ identifier: formatted, password });
      if (!data?.token) throw new Error('Login failed: missing token');
      persistEmployerSession(data.token, data.user || data);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const sendOtpToPhone = async (e164) => {
    await authPublicApi.sendLoginOtp(e164);
    setOtpPhone(e164);
    setOtpCode('');
    setStep('otp');
    startOtpTimer();
  };

  const onSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!termsAccepted) {
      setError('Please agree to the Terms and Privacy Policy to continue');
      return;
    }
    if (!validateOtpPhoneForm()) return;
    const e164 = toE164IndiaTenDigits(phoneTen);
    if (!e164) return;
    try {
      setLoading(true);
      await sendOtpToPhone(e164);
    } catch (err) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const onVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (otpCode.length !== 6) {
      setError('Enter the 6-digit code');
      return;
    }
    try {
      setLoading(true);
      const data = await authPublicApi.verifyLoginOtp(otpPhone, otpCode);
      const token = data?.token;
      const u = data?.user;
      if (!token || !u) throw new Error('Verification failed');
      persistEmployerSession(token, { ...u, id: u.id, userType: u.userType });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Invalid or expired OTP');
    } finally {
      setLoading(false);
    }
  };

  const onResendOtp = async () => {
    if (resendLeft > 0 || loading) return;
    setError('');
    try {
      setLoading(true);
      await authPublicApi.sendLoginOtp(otpPhone);
      startOtpTimer();
    } catch (err) {
      setError(err.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const switchMethod = (m) => {
    setMethod(m);
    setError('');
    setStep('credentials');
    setOtpCode('');
    setOtpPhone('');
  };

  const botSrc = `${process.env.PUBLIC_URL}/android/iv_login_bot.png`;

  if (step === 'otp') {
    return (
      <div className="auth-shell emp-login-shell">
        <div className="auth-card emp-login-card">
          <button type="button" className="emp-login-back" onClick={() => setStep('credentials')} aria-label="Back">
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
          </button>
          <h1 className="emp-login-welcome">Verify OTP</h1>
          <p className="emp-login-otp-instruction">Enter the code we sent to your number below</p>
          <p className="emp-login-otp-number-line">
            Current mobile number <span className="emp-login-accent-strong">{displayE164(otpPhone)}</span>
          </p>
          <button type="button" className="emp-login-change-number" onClick={() => setStep('credentials')}>
            <PhoneIcon />
            <span>Change Number</span>
          </button>
          <form onSubmit={onVerifyOtp} className="emp-login-form">
            <OtpDigitRow value={otpCode} onChange={setOtpCode} disabled={loading} />
            <div className="emp-login-resend-row">
              {resendLeft > 0 ? (
                <span className="emp-login-resend-timer">
                  Resend OTP in <span className="emp-login-accent-strong">{resendLeft}s</span>
                </span>
              ) : (
                <button type="button" className="emp-login-resend-btn" onClick={onResendOtp} disabled={loading}>
                  Resend OTP
                </button>
              )}
            </div>
            {error ? (
              <p className="emp-login-error" role="alert">
                {error}
              </p>
            ) : null}
            <button type="submit" className="emp-login-primary" disabled={loading || otpCode.length !== 6}>
              {loading ? 'Verifying…' : 'Verify OTP'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell emp-login-shell">
      <div className="auth-card emp-login-card">
        <InstallAppPrompt />
        <h1 className="emp-login-welcome">WELCOME to TipPay</h1>
        <div className="emp-login-hero-wrap">
          <img src={botSrc} alt="" className="emp-login-hero" width={200} height={200} />
        </div>

        <div className="emp-login-tabs" role="tablist" aria-label="Login method">
          <button
            type="button"
            role="tab"
            aria-selected={method === 'password'}
            className={`emp-login-tab${method === 'password' ? ' emp-login-tab--active' : ''}`}
            onClick={() => switchMethod('password')}
          >
            <LockIcon />
            <span>Password</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={method === 'otp'}
            className={`emp-login-tab${method === 'otp' ? ' emp-login-tab--active' : ''}`}
            onClick={() => switchMethod('otp')}
          >
            <PhoneIcon />
            <span>OTP</span>
          </button>
        </div>

        {method === 'password' ? (
          <form onSubmit={onPasswordSubmit} className="emp-login-form" noValidate>
            <label className="emp-login-field">
              <span className="emp-login-sr-only">Email or Phone Number</span>
              <PersonIcon />
              <input
                type="text"
                autoComplete="username"
                placeholder="Email or Phone Number"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
              />
            </label>
            <label className="emp-login-field">
              <span className="emp-login-sr-only">Password</span>
              <LockIcon />
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="emp-login-eye"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <EyeIcon off={!showPassword} />
              </button>
            </label>
            <div className="emp-login-forgot-row">
              <a href={SITE_URL} className="emp-login-link" target="_blank" rel="noopener noreferrer">
                Forgot Password?
              </a>
            </div>
            <label className="emp-login-terms">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="emp-login-checkbox"
              />
              <span>
                By continuing, you agree to our{' '}
                <a
                  href={TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="emp-login-inline-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms and Privacy Policy
                </a>
              </span>
            </label>
            {error ? (
              <p className="emp-login-error" role="alert">
                {error}
              </p>
            ) : null}
            <button type="submit" className="emp-login-primary" disabled={loading}>
              {loading ? 'Logging in…' : 'Login'}
            </button>
          </form>
        ) : (
          <form onSubmit={onSendOtp} className="emp-login-form" noValidate>
            <label className="emp-login-field">
              <span className="emp-login-sr-only">Phone Number</span>
              <PhoneIcon />
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="Phone Number"
                maxLength={10}
                value={phoneTen}
                onChange={(e) => setPhoneTen(e.target.value.replace(/\D/g, '').slice(0, 10))}
              />
            </label>
            <label className="emp-login-terms">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="emp-login-checkbox"
              />
              <span>
                By continuing, you agree to our{' '}
                <a
                  href={TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="emp-login-inline-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms and Privacy Policy
                </a>
              </span>
            </label>
            {error ? (
              <p className="emp-login-error" role="alert">
                {error}
              </p>
            ) : null}
            <button type="submit" className="emp-login-primary" disabled={loading}>
              {loading ? 'Sending…' : 'Send OTP'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default LoginPage;
